import { ChangeDetectorRef, Component, inject, OnDestroy } from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { Sort } from '@angular/material/sort';
import { ErzeugungsanlageService } from '../../service/erzeugungsanlage/erzeugungsanlage.service';
import { debounceTime, filter, map, shareReplay, skip, startWith, switchMap, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { RoutingService } from '../../service/common/routing/routing.service';
import { combineLatest, distinctUntilChanged, merge, Observable, ReplaySubject, Subject } from 'rxjs';
import { ErzeugungsanlageFilter } from '../../service/erzeugungsanlage/erzeugungsanlage-filter';
import { ErzeugungsanlagenRefreshService } from '../../service/erzeugungsanlage/erzeugungsanlagen-refresh.service';
import { TnbUser } from '../../service/auth/tnb-user';
import { Pageable } from '../../common/paging/pageable';
import { ErzeugungsanlagenPage } from '../../domain/utils/page';
import { TeilgebietSelectionService } from '../../service/common/teilgebiet/teilgebiet-selection.service';
import { Leistungseinheit } from '../../domain/model/leistungseinheit';
import { distinctUntilFilterChanged } from '../../common/util/helper';

@Component({
    selector: 'redispatch-erzeugungsanlage-list',
    templateUrl: './erzeugungsanlage-list.component.html',
    styleUrls: [
        './erzeugungsanlage-list.component.scss',
    ],
    standalone: false,
})
export class ErzeugungsanlageListComponent implements OnDestroy {

    public readonly PAGE_SIZE = 50;

    public page$: Observable<Pageable>;
    public filter$: Observable<ErzeugungsanlageFilter>;
    public resolvedErzeugungsanlagenPage: ErzeugungsanlagenPage<any>;

    public readonly profile: TnbUser;

    private componentDestroyed = false;

    private pageFromUserInteraction$: Observable<Pageable>;
    private pageEventFromUserInteraction$ = new ReplaySubject<PageEvent>(1);
    private sortFromUserInteraction$ = new ReplaySubject<Sort>(1);
    private filterFromUserInteraction$ = new Subject<ErzeugungsanlageFilter>();

    private route = inject(ActivatedRoute);
    private router = inject(Router);
    private routeService = inject(RoutingService);
    private erzeugungsanlageService = inject(ErzeugungsanlageService);
    private erzeugungsanlagenRefreshService = inject(ErzeugungsanlagenRefreshService);
    private teilgebietSelectionService = inject(TeilgebietSelectionService);
    private changeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        this.profile = this.route.snapshot.data.profile;

        this.setupFilterObservable();
        this.setupPageObservable();
        this.setupErzeugungsanlageObservable();
    }

    ngOnDestroy(): void {
        this.componentDestroyed = true;
    }

    private setupPageObservable() {
        this.pageFromUserInteraction$ = combineLatest([
            this.sortFromUserInteraction$,
            this.pageEventFromUserInteraction$,
        ])
            .pipe(
                map(params => {
                    const pageable = new Pageable();

                    const sort = params[0];
                    const page = params[1];

                    pageable.sort = sort?.active || '';
                    pageable.direction = sort?.direction || '';
                    pageable.page = page?.pageIndex || 0;

                    return pageable;
                }),
                shareReplay(1),
            );

        this.route.queryParams
            .pipe(
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(params => {
                this.sortFromUserInteraction$.next({
                    active: params['sort'],
                    direction: params['direction'],
                });
                this.pageEventFromUserInteraction$.next({
                    pageIndex: +params['page'],
                } as PageEvent);
            });

        this.page$ = this.pageFromUserInteraction$
            .pipe(
                distinctUntilFilterChanged(),
                shareReplay(1),
            );
    }

    private setupFilterObservable() {
        const filterFromQueryParam$ = this.route.queryParams
            .pipe(
                map((params: any) => {
                    const eaFilter = new ErzeugungsanlageFilter();
                    if (params) {
                        eaFilter.netzbetreiberName = params['netzbetreiberName'];
                        eaFilter.netzbetreiberTeilgebiet = params['netzbetreiberTeilgebiet'];
                        eaFilter.dokumentenId = params['dokumentenId'];
                        eaFilter.steuerbareResourceId = params['steuerbareResourceId'];
                        eaFilter.technischeResourceId = params['technischeResourceId'];
                        eaFilter.bezVnbIntern = params['bezVnbIntern'];
                        eaFilter.ortsname = params['ortsname'];
                        eaFilter.stationsname = params['stationsname'];
                        eaFilter.trafoFeldName = params['trafoFeldName'];
                        eaFilter.energietraeger = params['energietraeger'];
                        eaFilter.status = params['status'] ? JSON.parse(params['status']) : undefined;
                        if (params['installierteLeistungMin']) {
                            const min = Number(params['installierteLeistungMin']);
                            eaFilter.installierteLeistungMin = isNaN(min) ? undefined : min;
                        }
                        if (params['installierteLeistungMax']) {
                            const max = Number(params['installierteLeistungMax']);
                            eaFilter.installierteLeistungMax = isNaN(max) ? undefined : max;
                        }
                    }

                    if (this.profile.netzbetreiberType !== 'WESTNETZ') {
                        eaFilter.stadtwerkeId = this.teilgebietSelectionService.getSelectedStadtwerkeId();
                    }
                    return eaFilter;
                }),
            );

        this.filter$ = merge(
            this.filterFromUserInteraction$,
            filterFromQueryParam$,
        ).pipe(
            distinctUntilFilterChanged(),
            shareReplay(1),
        );
    }

    private setupErzeugungsanlageObservable() {
        // Goto first page, when filter changes
        this.filterFromUserInteraction$
            .pipe(
                skip(1),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(_filter => {
                const page = new PageEvent();
                page.pageIndex = 0;
                this.pageEventFromUserInteraction$.next(page);
            });

        combineLatest([
            this.pageFromUserInteraction$,
            this.filterFromUserInteraction$,
        ])
            .pipe(
                filter(() => {
                    const currentParams = this.route.snapshot.queryParams;
                    return !currentParams['erzeugungsanlage'] && !currentParams['createnew'];
                }),
                map(param => {
                    const pageable = param[0];
                    const eaFilter = param[1];
                    return this.routeService.erzeugungsanlageTable(pageable, eaFilter);
                }),
                distinctUntilChanged(),
                takeWhile(() => !this.componentDestroyed),
            )
            .subscribe(url => {
                this.router.navigateByUrl(url);
            });

        combineLatest([
            this.page$,
            this.filter$,
            this.erzeugungsanlagenRefreshService.refreshRequested$.pipe(startWith(null)),
        ])
            .pipe(
                debounceTime(0),
                takeWhile(() => !this.componentDestroyed),
                switchMap(param => this.erzeugungsanlageService.getErzeugungsanlagen(param[0], param[1])),
            )
            .subscribe(page => {
                this.resolvedErzeugungsanlagenPage = page;
                this.changeDetectorRef.markForCheck();
            });
    }

    filterChanged(eaFilter: ErzeugungsanlageFilter) {
        this.filterFromUserInteraction$.next(eaFilter);
    }

    pageChange($event: PageEvent) {
        this.pageEventFromUserInteraction$.next($event);
    }

    sortChange($event: Sort) {
        this.sortFromUserInteraction$.next($event);
    }

    protected readonly Leistungseinheit = Leistungseinheit;
}
